import { ThemeKey } from "@/helper/LocalStorageKeys.ts";
import { useLocalStorage } from "@vueuse/core";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export enum ThemeId {
  LIGHT = "light",
  DARK = "dark",
  SYSTEM = "system",
}

interface Theme {
  id: ThemeId;
  name: string;
  icon: string;
  color: string;
}

export const themes: Record<ThemeId, Theme> = {
  [ThemeId.LIGHT]: {
    id: ThemeId.LIGHT,
    name: "Tag",
    icon: "sun",
    color: "#FFB700",
  },
  [ThemeId.DARK]: {
    id: ThemeId.DARK,
    name: "Nacht",
    icon: "moon",
    color: "#85A0CC",
  },
  [ThemeId.SYSTEM]: {
    id: ThemeId.SYSTEM,
    name: "System",
    icon: "desktop",
    color: "#999999",
  },
};

function getTheme(id: string): Theme {
  if (id === ThemeId.SYSTEM) {
    return themes[ThemeId.SYSTEM];
  } else if (id === ThemeId.DARK) {
    return themes[ThemeId.DARK];
  } else if (id === ThemeId.LIGHT) {
    return themes[ThemeId.LIGHT];
  } else {
    return themes[ThemeId.SYSTEM];
  }
}

export const useThemeStore = defineStore("theme", () => {
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  const current = useLocalStorage<string>(ThemeKey, themes[ThemeId.SYSTEM].id);
  const currentThemeData = computed(() => ({ ...getTheme(current.value) }));
  const isDarkMode = ref(false);

  function calculateValue(): boolean {
    if (currentThemeData.value.id === ThemeId.LIGHT) {
      return false;
    } else if (currentThemeData.value.id === ThemeId.DARK) {
      return true;
    } else {
      return mediaQuery.matches;
    }
  }

  function setDarkMode(on: boolean): void {
    isDarkMode.value = on;
    document.documentElement.classList.toggle("dark", on);
  }

  function selectTheme(id: ThemeId) {
    current.value = id;
    setDarkMode(calculateValue());
  }

  function updateDarkModeFromSystem(event: MediaQueryListEvent) {
    if (current.value === ThemeId.SYSTEM) {
      setDarkMode(event.matches);
    }
  }

  // init
  mediaQuery.addEventListener("change", updateDarkModeFromSystem);
  setDarkMode(calculateValue());

  return { current: currentThemeData, select: selectTheme, isDarkMode };
});
